import React from "react";
import {
  useReactTable,
  getCoreRowModel,
  type ColumnDef,
  type PaginationState,
  type Updater,
  type SortingState,
} from "@tanstack/react-table";

import { Button, ButtonGroup } from "react-bootstrap";

import { humanFileSize } from "../../utils/helper";
import type z from "zod";
import { schemas } from "../../api/generated";
import { ActivityDate } from "../ActivityDate";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faUserPlus } from "@fortawesome/free-solid-svg-icons";
import { DataTable } from "../DataTable";
import { buildUserCountText } from "../../utils/buildUserCountText";

type FileDTO = z.infer<typeof schemas.FileWithUsage>;

interface FilesTableProps {
  files: FileDTO[];
  totalFiles: number;

  // eslint-disable-next-line no-unused-vars
  onAssign?: (file: FileDTO) => void;
  // eslint-disable-next-line no-unused-vars
  onDelete?: (file: FileDTO) => void;

  onTableChange: (
    // eslint-disable-next-line no-unused-vars
    pagination: Updater<PaginationState>,
    // eslint-disable-next-line no-unused-vars
    sorting: Updater<SortingState>
  ) => void;

  pageIndex: number;
  pageSize: number;
  sortBy?: string;
  sortDescending?: boolean;
}

interface FilesTableMeta {
  // eslint-disable-next-line no-unused-vars
  onAssign?: (file: FileDTO) => void;
  // eslint-disable-next-line no-unused-vars
  onDelete?: (file: FileDTO) => void;
}

const columns: ColumnDef<FileDTO, unknown>[] = [
  {
    accessorKey: "fileName",
    header: () => "File Name",
    cell: (info) => (
      <a className="font-monospace" href={`/files/${info.getValue<string>()}`}>
        {info.getValue<string>()}
      </a>
    ),
    minSize: 150,
  },
  {
    accessorKey: "fileType",
    header: () => "File Type",
    minSize: 80,
    maxSize: 260,
  },
  {
    accessorKey: "fileSize",
    header: () => "File Size",
    cell: (info) => humanFileSize(info.getValue<number>()),
    minSize: 89,
    maxSize: 120,
  },
  {
    accessorKey: "createdAt",
    header: () => "Created",
    cell: (info) => <ActivityDate date={info.getValue<string>()} />,
    minSize: 100,
    maxSize: 123,
  },
  {
    accessorKey: "assignmentCount",
    header: () => "Assignments",
    cell: (info) => buildUserCountText({ count: info.getValue<number>() }),
    size: 121,
  },
  {
    id: "actions",
    header: "Actions",
    enableSorting: false,
    enableResizing: true,
    cell: ({ row, table }) => {
      const file = row.original;
      const meta = table.options.meta as FilesTableMeta;

      const onAssign = meta?.onAssign;
      const onDelete = meta?.onDelete;

      return (
        <ButtonGroup
          className="d-block mx-auto"
          style={{
            // Work around for built in cell padding
            marginTop: "-6px",
            marginBottom: "-6px",
          }}
        >
          <Button
            name={`assign-button-${row.index}`}
            variant="outline-dark"
            size="sm"
            title="Assign to a user"
            aria-label="Assign to a user"
            disabled={false}
            onClick={() => onAssign?.(file)}
          >
            <FontAwesomeIcon width={20} icon={faUserPlus} />
          </Button>
          <Button
            variant="outline-dark"
            size="sm"
            title="Delete file"
            aria-label="Delete file"
            onClick={() => {
              onDelete?.(file);
            }}
          >
            <FontAwesomeIcon width={20} icon={faTrash} />
          </Button>
        </ButtonGroup>
      );
    },
    meta: {
      width: "min-content",
    },
  },
];

export function FilesTable({
  files,
  totalFiles,
  onAssign,
  onDelete,
  onTableChange,
  pageIndex,
  pageSize,
  sortBy,
  sortDescending,
}: FilesTableProps): React.ReactNode {
  const table = useReactTable<FileDTO>({
    data: files,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange: (pagination) => {
      onTableChange(pagination, (prev) => prev);
    },
    onSortingChange: (sorting) => {
      onTableChange((prev) => ({ ...prev, pageIndex: 0 }), sorting);
    },
    enableColumnResizing: true,
    meta: {
      onAssign,
      onDelete,
    },
    initialState: {
      columnSizing: {
        fileName: 350,
        fileType: 150,
        fileSize: 100,
        createdAt: 150,
        actions: 100,
      },
    },
    manualPagination: true,
    manualSorting: true,
    rowCount: totalFiles,
    state: {
      pagination: { pageIndex, pageSize },
      sorting: sortBy
        ? [
            {
              id: sortBy,
              desc: sortDescending || false,
            },
          ]
        : [],
    },
    defaultColumn: {
      size: Number.NaN,
      minSize: Number.NaN,
      maxSize: Number.NaN,
    },
  });

  return <DataTable table={table} itemName="file" />;
}
